import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useCompare } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  DaysSelect,
  Spinner,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function ComparePage() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useCompare(days);
  const rows = data?.data ?? [];

  const avgKWData = rows.map((r: any) => ({
    meter: r.meter_id,
    value: r.avg_kw,
  }));
  const uptimeData = rows.map((r: any) => ({
    meter: r.meter_id,
    value: r.uptime_pct,
  }));

  const tooltip =
    (color: string, unit: string) =>
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="ra-tooltip">
          <p className="text-slate-500">{label}</p>
          <p className={`font-mono font-semibold`} style={{ color }}>
            {payload[0]?.value?.toFixed(2)} {unit}
          </p>
        </div>
      );
    };

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Multi-Meter Comparison"
          subtitle="Side-by-side stats across all meters"
        >
          <DaysSelect
            value={days}
            onChange={setDays}
            options={[3, 7, 14, 30]}
          />
        </PageHeader>

        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <ChartCard title="Avg Active Power per Meter" subtitle="W">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={avgKWData}
                    margin={{ top: 8, right: 8, left: 0, bottom: 40 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={CHART_COLORS.grid}
                    />
                    <XAxis
                      dataKey="meter"
                      tick={{ fill: "#64748B", fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#64748B", fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={tooltip(CHART_COLORS.blue, "W")} />
                    <Bar
                      dataKey="value"
                      fill={CHART_COLORS.blue}
                      radius={[4, 4, 0, 0]}
                      name="Avg KW"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Uptime % per Meter">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={uptimeData}
                    margin={{ top: 8, right: 8, left: 0, bottom: 40 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={CHART_COLORS.grid}
                    />
                    <XAxis
                      dataKey="meter"
                      tick={{ fill: "#64748B", fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fill: "#64748B", fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={tooltip(CHART_COLORS.emerald, "%")} />
                    <Bar
                      dataKey="value"
                      fill={CHART_COLORS.emerald}
                      radius={[4, 4, 0, 0]}
                      name="Uptime"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <div className="ra-card">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">
                Full Comparison Table
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-200">
                      {[
                        "Meter ID",
                        "Avg KW (W)",
                        "Avg Voltage (V)",
                        "Avg PF",
                        "Total kWh",
                        "Uptime",
                        "Anomalies",
                      ].map((h) => (
                        <th key={h} className="text-left pb-2 pr-4 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r: any, i: number) => (
                      <tr
                        key={i}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-2 pr-4 font-mono text-blue-600 font-semibold">
                          {r.meter_id}
                        </td>
                        <td className="py-2 pr-4 font-mono text-slate-800">
                          {fmt.num(r.avg_kw, 2)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-slate-800">
                          {fmt.num(r.avg_voltage, 2)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-slate-800">
                          {fmt.num(Math.abs(r.avg_power_factor), 3)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-slate-800">
                          {fmt.num(r.total_kwh_delta, 3)}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={
                              r.uptime_pct > 95
                                ? "ra-badge-green"
                                : r.uptime_pct > 80
                                  ? "ra-badge-yellow"
                                  : "ra-badge-red"
                            }
                          >
                            {fmt.pct(r.uptime_pct)}
                          </span>
                        </td>
                        <td className="py-2">
                          <span
                            className={
                              r.anomaly_count > 5
                                ? "ra-badge-red"
                                : r.anomaly_count > 0
                                  ? "ra-badge-yellow"
                                  : "ra-badge-green"
                            }
                          >
                            {r.anomaly_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
